import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useMyClinicRole } from '@/hooks/useMyClinicRole';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Save, Eye, EyeOff } from 'lucide-react';

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  staff: 'Staff',
};

export default function SettingsUserProfile() {
  const { user, profile, refreshProfile } = useAuth();
  const { role, isLoading: roleLoading } = useMyClinicRole();
  const { toast } = useToast();

  // Personal info state
  const [fullName, setFullName] = useState('');
  const [credentials, setCredentials] = useState('');
  const [saving, setSaving] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name ?? '');
      // credentials comes from the DB but isn't in the typed Profile interface
      setCredentials((profile as any).credentials ?? '');
    }
  }, [profile]);

  async function handleSaveInfo(e: React.FormEvent) {
    e.preventDefault();
    if (!fullName.trim()) {
      toast({ title: 'Full name is required', variant: 'destructive' });
      return;
    }
    if (!profile) return;
    setSaving(true);
    try {
      const { error } = await (supabase as any)
        .from('profiles')
        .update({ full_name: fullName.trim(), credentials: credentials.trim() || null })
        .eq('id', profile.id);
      if (error) throw error;
      await refreshProfile();
      toast({ title: 'Profile updated' });
    } catch (err: any) {
      toast({ title: 'Failed to update profile', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!currentPassword) {
      toast({ title: 'Current password is required', variant: 'destructive' });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: 'New password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }

    setChangingPassword(true);
    try {
      // Verify current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email ?? '',
        password: currentPassword,
      });
      if (signInError) {
        toast({ title: 'Current password is incorrect', variant: 'destructive' });
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;

      toast({ title: 'Password updated successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      toast({ title: 'Failed to update password', description: err.message, variant: 'destructive' });
    } finally {
      setChangingPassword(false);
    }
  }

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Section 1: Personal Information */}
      <div>
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground">Personal Information</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Your name and credentials as displayed across the platform
          </p>
        </div>

        <form onSubmit={handleSaveInfo} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="full-name">
              Full Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="full-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
              className="border-gray-200 dark:border-border bg-white dark:bg-background focus:border-primary focus:ring-primary"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="credentials">Credentials</Label>
            <Input
              id="credentials"
              value={credentials}
              onChange={(e) => setCredentials(e.target.value)}
              placeholder="e.g. Dr., MD, RN, LCSW, PhD"
              className="border-gray-200 dark:border-border bg-white dark:bg-background focus:border-primary focus:ring-primary"
            />
            <p className="text-xs text-muted-foreground">
              Optional — displayed alongside your name
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input
              value={user?.email ?? ''}
              disabled
              className="border-gray-200 dark:border-border bg-muted"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Role</Label>
            <div>
              <Badge variant="secondary" className="text-xs">
                {role ? ROLE_LABELS[role] ?? role : '—'}
              </Badge>
            </div>
          </div>

          <div className="pt-2">
            <Button type="submit" disabled={saving}>
              {saving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
              ) : (
                <><Save className="mr-2 h-4 w-4" /> Save Changes</>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Divider */}
      <div className="border-t" />

      {/* Section 2: Change Password */}
      <div>
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground">Change Password</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Update your account password
          </p>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="current-password">Current Password</Label>
            <div className="relative">
              <Input
                id="current-password"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="border-gray-200 dark:border-border bg-white dark:bg-background pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-password">New Password</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="border-gray-200 dark:border-border bg-white dark:bg-background pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Minimum 8 characters</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm-password">Confirm New Password</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="border-gray-200 dark:border-border bg-white dark:bg-background pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="pt-2">
            <Button type="submit" disabled={changingPassword}>
              {changingPassword ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...</>
              ) : (
                'Update Password'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
